import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiSave, FiFolder } = FiIcons;

const FOLDER_COLORS = [
  { name: 'Emerald', hex: '#10b981' },
  { name: 'Cyan', hex: '#06b6d4' },
  { name: 'Violet', hex: '#8b5cf6' },
  { name: 'Amber', hex: '#f59e0b' },
  { name: 'Rose', hex: '#f43f5e' },
  { name: 'Cobalt', hex: '#3b82f6' },
];

const FOLDER_GRADIENTS = [
  { name: 'Neon Cyberpunk', value: 'linear-gradient(90deg, #10b981 0%, #3b82f6 100%)' },
  { name: 'Emerald Matrix', value: 'linear-gradient(90deg, #059669 0%, #10b981 50%, #047857 100%)' },
  { name: 'Cosmic Nebula', value: 'linear-gradient(90deg, #8b5cf6 0%, #ec4899 100%)' },
  { name: 'Solar Flare', value: 'linear-gradient(90deg, #f59e0b 0%, #ef4444 100%)' },
  { name: 'Deep Abyss', value: 'linear-gradient(90deg, #06b6d4 0%, #3b82f6 50%, #1e1b4b 100%)' },
];

export default function AddFolderModal({ isOpen, onClose, onSave, themeColors: tc, isDark: isDarkProp }) {
  const [title, setTitle] = useState('');
  const [selectedColor, setSelectedColor] = useState(FOLDER_COLORS[0].hex);
  const [selectedGradient, setSelectedGradient] = useState(FOLDER_GRADIENTS[0].value);
  const prevOpen = useRef(false);
  const inputRef = useRef(null);

  const isDark = isDarkProp !== undefined
    ? Boolean(isDarkProp)
    : !tc?.card?.toLowerCase().includes('ffffff') && !tc?.card?.startsWith('rgba(255');
  const inputBg = isDark ? 'rgba(0,0,0,0.35)' : '#FFFFFF';
  const inputBorder = isDark ? tc.border : '#CBD5E1';

  useEffect(() => {
    if (isOpen && !prevOpen.current) {
      setTitle('');
      setSelectedColor(FOLDER_COLORS[0].hex);
      setTimeout(() => inputRef.current?.focus(), 80);
    }
    prevOpen.current = isOpen;
  }, [isOpen]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50" />
          <motion.div initial={{ opacity: 0, scale: 0.9, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
            <div className="border rounded-2xl shadow-2xl w-full max-w-sm pointer-events-auto overflow-hidden"
              style={{ backgroundColor: tc.card, borderColor: tc.border }}>

              {/* Header */}
              <div className="flex items-center justify-between px-6 pt-5 pb-5">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-xl flex items-center justify-center shadow-sm"
                    style={{ backgroundColor: `${selectedColor}22`, border: `1px solid ${selectedColor}55` }}>
                    <SafeIcon icon={FiFolder} className="w-4 h-4" style={{ color: selectedColor }} />
                  </div>
                  <h2 className="text-lg font-semibold" style={{ color: tc.text }}>New Folder</h2>
                </div>
                <button onClick={onClose} style={{ color: tc.muted }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = tc.text)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = tc.muted)}>
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                if (title.trim()) onSave({ type: 'folder', title: title.trim(), color: selectedColor, gradient: selectedGradient });
              }}>
                <div className="px-6 pb-2 space-y-4">
                  <div>
                    <label className="block text-xs font-medium mb-1.5" style={{ color: tc.muted }}>Folder Name</label>
                    <input ref={inputRef} type="text" value={title} onChange={(e) => setTitle(e.target.value)}
                      required placeholder="e.g. Design Tools"
                      className="w-full border rounded-xl px-4 py-3 text-sm focus:outline-none transition-colors"
                      style={{ backgroundColor: inputBg, borderColor: inputBorder, color: tc.text }}
                      onFocus={(e) => (e.currentTarget.style.borderColor = selectedColor)}
                      onBlur={(e) => (e.currentTarget.style.borderColor = inputBorder)} />
                  </div>

                  <div>
                    <label className="block text-xs font-medium mb-2" style={{ color: tc.muted }}>Custom Accent Glow</label>
                    <div className="flex items-center justify-between gap-1.5 p-2 rounded-xl border"
                      style={{ backgroundColor: inputBg, borderColor: inputBorder }}>
                      {FOLDER_COLORS.map((c) => (
                        <button
                          key={c.hex}
                          type="button"
                          onClick={() => setSelectedColor(c.hex)}
                          className="w-7 h-7 rounded-lg flex items-center justify-center transition-all cursor-pointer"
                          style={{
                            backgroundColor: c.hex,
                            transform: selectedColor === c.hex ? 'scale(1.15)' : 'scale(1)',
                            boxShadow: selectedColor === c.hex ? `0 0 10px ${c.hex}` : 'none',
                            border: selectedColor === c.hex ? '2px solid #fff' : '2px solid transparent',
                          }}
                          title={c.name}
                        />
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium mb-2" style={{ color: tc.muted }}>Cyber Gradient Banner</label>
                    <div className="space-y-1.5 p-2 rounded-xl border"
                      style={{ backgroundColor: inputBg, borderColor: tc.border }}>
                      {FOLDER_GRADIENTS.map((g) => (
                        <button
                          key={g.name}
                          type="button"
                          onClick={() => setSelectedGradient(g.value)}
                          className={`w-full h-6 rounded-lg flex items-center justify-between px-2 text-[10px] font-mono transition-all cursor-pointer ${
                            selectedGradient === g.value ? 'ring-2 ring-white font-bold' : 'opacity-70 hover:opacity-100'
                          }`}
                          style={{ background: g.value }}
                        >
                          <span className="text-white drop-shadow">{g.name}</span>
                          {selectedGradient === g.value && <span className="text-white">✓</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex gap-3 px-6 py-4 border-t mt-4" style={{ borderColor: tc.border }}>
                  <button type="button" onClick={onClose}
                    className="px-4 py-2.5 rounded-lg text-sm border transition-colors"
                    style={{ borderColor: tc.border, color: tc.muted }}>
                    Cancel
                  </button>
                  <button type="submit"
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm"
                    style={{ backgroundColor: tc.accent, color: '#000' }}>
                    <SafeIcon icon={FiSave} className="w-4 h-4" /> Create Folder
                  </button>
                </div>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}